// components/avatar-creator/AvatarCreator.js
import React, { useEffect, useRef, useState } from "react";

import avatarParts from "./avatarParts";
import "./AvatarCreator.scss";
import { db } from "../../firebase/firebase.utils";
import createTinyAvatarFile from "../../utils/createTinyAvatarFile";
import Modal from "../modal/modal.component";
import Peep from "./Peep";
import { IoShuffle } from "react-icons/io5";

const SKIN_COLORS = [
  "#694D3D",
  "#AE5D29",
  "#D08B5B",
  "#EDB98A",
  "#FFDBB4",
  "#FFD11B",
  "#FFFFFF",
];

const HAIR_COLORS = [
  "#000000",
  "#2C1B18",
  "#4A312C",
  "#724133",
  "#A55728",
  "#B58143",
  "#C93305",
  "#D6B370",
  "#E8E1E1",
  "#ECDCBF",
  "#F59797",
];

const DEFAULT_AVATAR = {
  head: "Bun",
  accessories: "None",
  body: "StripedPocketTee",
  facialHair: "None",
  face: "Blank",
  skinColor: "#FFDBB4",
  hairColor: "#000000",
};

const normalizeColor = (color, fallback) => {
  if (typeof color !== "string" || !color.trim()) return fallback;

  const value = color.trim();
  return value.startsWith("#") ? value : `#${value}`;
};

const normalizePart = (category, part, fallback) =>
  part && avatarParts[category]?.[part] ? part : fallback;

const normalizeAvatar = (value = {}) => ({
  ...DEFAULT_AVATAR,
  ...value,
  head: normalizePart("head", value.head, DEFAULT_AVATAR.head),
  accessories: normalizePart(
    "accessories",
    value.accessories,
    DEFAULT_AVATAR.accessories,
  ),
  body: normalizePart("body", value.body, DEFAULT_AVATAR.body),
  facialHair: normalizePart(
    "facialHair",
    value.facialHair,
    DEFAULT_AVATAR.facialHair,
  ),
  face: normalizePart("face", value.face, DEFAULT_AVATAR.face),
  skinColor: normalizeColor(value.skinColor, DEFAULT_AVATAR.skinColor),
  hairColor: normalizeColor(value.hairColor, DEFAULT_AVATAR.hairColor),
});

const isNonePart = (part) =>
  typeof part === "string" && part.toLowerCase().includes("none");

const AvatarCreator = ({ currentUser, uploadImage }) => {
  const [showAvatarCreator, setShowAvatarCreator] = useState(false);
  const [avatar, setAvatar] = useState(DEFAULT_AVATAR);

  const tinyAvatarRef = useRef(null);

  useEffect(() => {
    setAvatar(normalizeAvatar(currentUser?.imgAvatar));
  }, [currentUser?.imgAvatar]);

  const handleChange = (category, value) => {
    setAvatar((current) => ({
      ...current,
      [category]: value,
    }));
  };

  const saveAvatar = async () => {
    if (!currentUser?.uid || !uploadImage) return;

    const normalizedAvatar = normalizeAvatar(avatar);

    try {
      const svg = tinyAvatarRef.current?.querySelector("svg");

      const avatarFile = await createTinyAvatarFile({
        svg,
        fileName: `avatarTiny-${currentUser.uid}.png`,
        outputSize: 256,
        flip: true,
      });

      const uploaded = await uploadImage(avatarFile, {
        fixedName: `avatarTiny-${currentUser.uid}.png`,
      });

      if (!uploaded?.url) {
        throw new Error("Avatar upload returned no URL");
      }

      await db.collection("users").doc(currentUser.uid).set(
        {
          imgAvatar: normalizedAvatar,
          avatarTinyUrl: uploaded.url,
        },
        { merge: true },
      );

      setAvatar(normalizedAvatar);

      alert("Avatar saved successfully!");
    } catch (error) {
      console.error("Error saving avatar:", error);
      alert("Error saving avatar.");
    }
  };

  const createRandomAvatar = () => {
    const randomValue = (category) => {
      const keys = Object.keys(avatarParts[category] || {});
      return keys[Math.floor(Math.random() * keys.length)];
    };

    setAvatar({
      head: randomValue("head"),
      accessories: randomValue("accessories"),
      body: randomValue("body"),
      facialHair: randomValue("facialHair"),
      face: randomValue("face"),
      skinColor: SKIN_COLORS[Math.floor(Math.random() * SKIN_COLORS.length)],
      hairColor: HAIR_COLORS[Math.floor(Math.random() * HAIR_COLORS.length)],
    });
  };

  const renderPart = (category, part) => {
    if (isNonePart(part)) {
      return <span className="avatar-none-option">None</span>;
    }

    const Component = avatarParts[category]?.[part];
    if (!Component) return null;

    const fill =
      category === "facialHair" ? avatar.hairColor : avatar.skinColor;

    return (
      <svg
        className="avatar-part-svg"
        width="180"
        height="100"
        viewBox="0 0 625 825"
        aria-hidden="true"
      >
        <g transform="translate(0, 50)">
          <Component
            fill={fill}
            skinColor={avatar.skinColor}
            hairColor={avatar.hairColor}
          />
        </g>
      </svg>
    );
  };

  const renderOptions = (category, label, className = "") => (
    <section className="avatar-option-section">
      <label>{label}</label>

      <div className={`option-slider ${className}`.trim()}>
        {Object.keys(avatarParts[category] || {}).map((key) => (
          <button
            key={key}
            type="button"
            className="avatar-part-option"
            aria-label={`Select ${label.toLowerCase()} ${key}`}
            aria-pressed={avatar[category] === key}
            onClick={() => handleChange(category, key)}
          >
            {renderPart(category, key)}
          </button>
        ))}
      </div>
    </section>
  );

  return (
    <div className="avatar_creator_container">
      <div
        ref={tinyAvatarRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          left: -10000,
          top: -10000,
          width: 256,
          height: 256,
          overflow: "visible",
          pointerEvents: "none",
        }}
      >
        <Peep
          useravatar={avatar}
          size={256}
          width={256}
          height={256}
          borderRadius={128}
          borderWidth={0}
          overflow="visible"
          backgroundColor="transparent"
          tiny
          flip
        />
      </div>

      <button
        type="button"
        onClick={() => setShowAvatarCreator(true)}
        className="custom-avatar-btn"
        aria-label="Open avatar creator"
      >
        <Peep
          useravatar={avatar}
          size={280}
          width={280}
          height={280}
          borderRadius={140}
          borderWidth={0}
          overflow="visible"
          backgroundColor="lightgray"
          tiny={false}
        />

        <span className="custom-avatar-btn-label">Avatar</span>
      </button>

      <Modal
        isOpen={showAvatarCreator}
        onClose={() => setShowAvatarCreator(false)}
      >
        <div className="custom-avatar">
          <div className="avatar-initial">
            <div className="avatar-result">
              <Peep
                useravatar={avatar}
                size={180}
                width={150}
                height={180}
                borderRadius={12}
                borderWidth={0}
                overflow="hidden"
                backgroundColor="lightgray"
                tiny={false}
              />
            </div>

            <div className="avatar-initials-col">
              <div className="avatar-initials">
                <div className="avatar-tone-section">
                  <label>Tone:</label>

                  <div className="avatar-tone-options">
                    {SKIN_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        aria-label={`Select skin tone ${color}`}
                        aria-pressed={avatar.skinColor === color}
                        onClick={() => handleChange("skinColor", color)}
                        style={{ backgroundColor: color }}
                        className="avatar-color-option"
                      />
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  className="avatar-random-button"
                  onClick={createRandomAvatar}
                >
                  <IoShuffle size="2.2em" />
                  <span>Create Random</span>
                </button>
              </div>

              <div className="option-slider">
                {renderOptions("face", "Face:", "canvaswithsm")}
              </div>
            </div>
          </div>

          {renderOptions("head", "Head:", "canvaswitbg")}
          {renderOptions("body", "Body (Clothing):", "canvaswitbg")}

          <div className="avatar-secondary-options">
            {renderOptions("facialHair", "Facial Hair:", "canvaswithsm")}

            {renderOptions("accessories", "Accessories:", "canvaswithsm")}
          </div>

          <div className="avatar-save-row">
            <button
              type="button"
              className="retroButton avatar-save-button"
              onClick={saveAvatar}
            >
              Save Avatar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AvatarCreator;
